import { Button, ButtonIcon, Callout, css, EllipsisIcon, SelectInput, styled } from 'canoe';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ContentMetadata, ContentSegments } from '@candu-ai/content/types';
import { SegmentsDefinition } from '@candu-ai/segments/types';
import { useToggle, useWorkspacePath } from 'commons/hooks';
import { useAutoComplete } from 'craft/components/Embed/hooks/useAutoComplete';
import { useViewportDom } from 'craft/state';
import { contentPath } from 'dashboard/pages/Content';
import { DEFAULT_SEGMENT } from 'dashboard/pages/Content/constants';
import { OptionsWrapper } from 'styleguide/components/Input/Select';

const TOPBAR_HEIGHT = 40;

const InputWrapper = styled.div<{ displayMode: 'normal' | 'compact' }>`
  ${({ displayMode }) => css`
    flex-grow: 1;
    max-width: ${displayMode === 'normal' ? '250px' : 'none'};
  `}
`;

const Container = styled.div<{ ref: any }>`
  ${({ theme }) => css`
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;

    padding: 8px;
    height: ${TOPBAR_HEIGHT}px;

    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    z-index: 100;

    &:after {
      content: '';

      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;

      background-color: ${theme.colors.BrandPurpleL10};
      width: 100%;
      height: ${TOPBAR_HEIGHT}px;
    }

    ${OptionsWrapper} {
      font-size: 0.75em;
    }
  `}
`;

const CalloutWrapper = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spaces.xs};
    background-color: ${theme.colors.White};
    border: 1px solid ${theme.colors.BrandPurpleL10};
    border-radius: ${theme.borderRadius.small};
    position: relative;
    z-index: 10001;
    width: 300px;
    white-space: initial;
    display: flex;
    flex-direction: column;
    gap: ${theme.spaces.xs};
    align-items: stretch;
  `}
`;

const getSegments = (contentSegment: ContentSegments, segments: SegmentsDefinition[]) => {
  if (contentSegment.defaultDocumentId) {
    if (!contentSegment.segmentDocuments) {
      return [
        {
          value: contentSegment.defaultDocumentId,
          label: DEFAULT_SEGMENT.name,
        },
      ];
    }

    return [
      ...contentSegment.segmentDocuments.map(({ documentId, segmentId }) => ({
        value: documentId,
        label: segments.find(({ id }) => id === segmentId)?.name,
      })),
      {
        value: contentSegment.defaultDocumentId,
        label: DEFAULT_SEGMENT.name,
      },
    ];
  }

  return contentSegment.segmentDocuments.map(({ documentId, segmentId }) => ({
    value: documentId,
    label: segments.find(({ id }) => id === segmentId)?.name,
  }));
};

interface FloatingHeaderProps {
  slug: string;
  setSlug: (slug: string) => void;
  contentList: any[];
  contentSegment: ContentSegments | null;
  resource: SegmentsDefinition[];
  selectedContent: ContentMetadata;
  selectedDocument?: number | null;
  setSelectedDocument: (id: number) => void;
}

const FloatingHeaderImpl = ({
  setSlug,
  slug,
  contentList,
  contentSegment,
  resource,
  selectedContent,
  selectedDocument,
  setSelectedDocument,
}: FloatingHeaderProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contextButtonRef = useRef<HTMLButtonElement>(null);
  const workspacePath = useWorkspacePath();
  const href = workspacePath(contentPath(selectedContent.id));
  const { viewportWrapperEl } = useViewportDom();

  const latestDisplayMode = useRef<'normal' | 'compact'>('compact');
  const [displayMode, setDisplayMode] = useState<'normal' | 'compact'>('compact');
  const [calloutOpen, toggleCallout] = useToggle(false);

  const { options, loadOptions } = useAutoComplete({
    options: contentList,
    filter: (search) => (item) => item.name.toLowerCase().includes(search?.toLowerCase()),
  });

  const contentOptions = useMemo(
    () =>
      options.map((content): { value: string; label: string } => ({
        value: content.slug,
        label: content.name,
      })),
    [options],
  );

  const segmentOptions = useMemo(
    () => (contentSegment ? getSegments(contentSegment, resource) : []),
    [contentSegment, resource],
  );

  /**
   * Recalculate the number of visible tabs based on the available width
   */
  const recalculateDisplayMode = useCallback(() => {
    if (!wrapperRef.current) {
      return;
    }

    const totalWidth = wrapperRef.current.clientWidth;

    if (totalWidth <= 600) {
      // compact mode
      if (latestDisplayMode.current !== 'compact') {
        latestDisplayMode.current = 'compact';
        setDisplayMode('compact');
      }
    } else if (latestDisplayMode.current !== 'normal') {
      // normal mode
      latestDisplayMode.current = 'normal';
      setDisplayMode('normal');
    }
  }, []);

  const throttleRecalculateDisplayMode = useCallback(
    throttle(() => {
      recalculateDisplayMode();
    }, 250),
    [recalculateDisplayMode],
  );

  /**
   * When the header resizes, recalculate how it should display
   */
  useEffect(() => {
    if (!wrapperRef.current) {
      return () => null;
    }

    // make sure to recalculate nav arrows when the viewportContainer is resized
    const observer = new ResizeObserver(throttleRecalculateDisplayMode);
    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [throttleRecalculateDisplayMode]);

  useEffect(() => {
    if (displayMode !== 'compact' && calloutOpen) {
      toggleCallout();
    }

    if (latestDisplayMode.current !== displayMode) {
      // if these get out of sync, usually due to a new render, make sure they are in sync
      latestDisplayMode.current = displayMode;
    }
  }, [displayMode, calloutOpen]);

  const settings = useMemo(
    () => (
      <>
        <InputWrapper displayMode={displayMode}>
          <SelectInput<{ value: string; label: string }>
            clearable={false}
            onSelect={(selectedItem) => selectedItem && setSlug(selectedItem.value)}
            selectedItem={
              slug ? contentOptions.find((content) => content.value === slug) : undefined
            }
            items={contentOptions}
            placeholder="Select Content"
            scale="small"
            searchable
            searchInputProps={{
              placeholder: 'Search Content',
              scale: 'small',
            }}
            onFilter={(e) => loadOptions(e.target.value)}
            onClearFilter={() => loadOptions('')}
            data-craft-ignore-events
            usePortal={displayMode === 'normal'}
          />
        </InputWrapper>
        <InputWrapper displayMode={displayMode}>
          <SelectInput<{ value: number; label: string | undefined }>
            clearable={false}
            onSelect={(selectedItem) => selectedItem && setSelectedDocument(selectedItem.value)}
            selectedItem={
              selectedDocument
                ? segmentOptions.find((content) => content.value === selectedDocument)
                : undefined
            }
            items={segmentOptions}
            placeholder="Select Segment"
            scale="small"
            data-craft-ignore-events
            usePortal={displayMode === 'normal'}
          />
        </InputWrapper>
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            window.open(href, '_blank');
          }}
        >
          Open Content
        </Button>
      </>
    ),
    [slug, selectedDocument, options, loadOptions, segmentOptions, href, displayMode],
  );

  if (displayMode === 'compact') {
    return (
      <Container ref={wrapperRef}>
        <ButtonIcon
          ref={contextButtonRef}
          icon={EllipsisIcon}
          size="s"
          radius="small"
          color="white"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            toggleCallout();
          }}
          data-craft-ignore-events
        />

        {calloutOpen && (
          <Callout
            referenceElement={contextButtonRef.current}
            onClose={toggleCallout}
            modifiers={[
              {
                name: 'preventOverflow',
                options: {
                  boundary: viewportWrapperEl,
                  padding: 8,
                },
              },
            ]}
          >
            {({ ref, style, attributes }) => (
              <CalloutWrapper data-craft-ignore-events ref={ref} style={style} {...attributes}>
                {settings}
              </CalloutWrapper>
            )}
          </Callout>
        )}
      </Container>
    );
  }

  return (
    <Container ref={wrapperRef} data-craft-ignore-events>
      {settings}
    </Container>
  );
};

export const FloatingHeader = connectWithHammock({
  endpoint: ENDPOINTS.segmentsDefinition,
})(FloatingHeaderImpl);
